import { IGetOffersByLink, IResponse } from '@api/types';
import api from '@api';
import {
    SHORTLINK_HOSTS,
    SOCIAL_HOSTS,
    SOCIAL_HOSTS_WITH_PATH,
    STORES_HOSTS,
} from '@components/pages/placements/config';

export interface IValidate {
    valid: boolean;
    message?: string;
}

/**
 * Проверка ссылок на социальные сети
 * @param {URL} url
 * @returns {IValidate}
 */
export const validateSocialUrl = (url: URL, t: (key: string) => string): IValidate => {
    try {
        // Только хост соц. сетей
        if (SOCIAL_HOSTS.indexOf(url.hostname) !== -1 && url.pathname === '/') {
            return {
                valid: false,
                message: t('Please, specify your web-page/group/channel'),
            };
        }

        // Технические страницы соц. сетей
        if (SOCIAL_HOSTS_WITH_PATH.indexOf(url.hostname + url.pathname) !== -1) {
            return {
                valid: false,
                message: t('Please, specify your web-page/group/channel'),
            };
        }

        return {
            valid: true,
        };
    } catch (error) {
        console.error(error);

        return {
            valid: false,
            message: t('Wrong url address'),
        };
    }
};

/**
 * Проверяем URL на валидность
 * @param {string} url
 * @returns {IValidate}
 */
export const validateUrl = (url: string, t: (key: string) => string): IValidate => {
    if (typeof url !== 'string')
        return {
            valid: false,
            message: t('Wrong url address'),
        };

    try {
        // eslint-disable-next-line no-useless-escape
        const pattern = /^(?:https?:\/\/)(?:www\.)?((?:[_-а-яa-z0-9]+[\.])+[а-яa-z]{2,17})(\/[^\s<>\"\,\{\}\\|\\\^\[\]`]*)?$/gi;
        const protocolCountsPattern = /(https?:\/\/)/g;
        const protocolCounts = ((url || '').match(protocolCountsPattern) || []).length;

        const result = pattern.test(url) && protocolCounts === 1;

        return {
            valid: result,
            message: !result
                ? t('Wrong link format, check for http / https at the beginning of the link')
                : undefined,
        };
    } catch (error) {
        console.error(error);

        return {
            valid: false,
            message: t('Wrong url address'),
        };
    }
};

/**
 * Сверяем url со всеми офферами
 * @param {URL} url
 * @param {number} currentOfferId
 * @returns {IValidate}
 */
export const compareUrlWithOffersUrl = async (
    url: URL,
    currentOfferId: number,
    t: (key: string) => string,
): Promise<IValidate> => {
    if (!(url instanceof URL)) {
        return {
            valid: false,
            message: t('Wrong url address'),
        };
    }

    try {
        const resp: IResponse<IGetOffersByLink> = await api.offer.getOfferByLink({
            params: { link: url },
        });
        const offersByLink = Array.isArray(resp.data) && resp.data.map(item => item.id);

        if (offersByLink && offersByLink.includes(currentOfferId)) {
            return {
                valid: false,
                // eslint-disable-next-line prettier/prettier
                message: t(
                    "Submitting the store's website as a platform is prohibited. We draw your attention to the fact that it is necessary to indicate the sites from which you plan to send traffic: your sites, publics and groups, as well as search advertising systems Yandex.Direct, Google Adwords, Target@mail.Ru, Begun, etc. If you have any questions, write to technical support",
                ),
            };
        }

        return {
            valid: true,
        };
    } catch (e) {
        console.error(e);
        return {
            valid: false,
            message: t('Wrong url address'),
        };
    }
};

/**
 * Проверяем ссылки на магазины приложений и расширений
 * @param {URL} url
 * @returns {IValidate}
 */
export const checkStoresUrl = (url: URL, t: (key: string) => string): IValidate => {
    try {
        if (STORES_HOSTS.indexOf(url.hostname) !== -1 && url.pathname === '/') {
            return {
                valid: false,
                message: t('Please add the link to your app or extension'),
            };
        }

        return {
            valid: true,
        };
    } catch (error) {
        console.error(error);

        return {
            valid: false,
            message: t('Wrong url address'),
        };
    }
};

/**
 * Проверяем ссылку на наличие сокращенного домена
 * @param {URL} url
 * @returns {IValidate}
 */
export const checkShortLinks = (url: URL, t: (key: string) => string): IValidate => {
    try {
        if (SHORTLINK_HOSTS.indexOf(url.hostname) !== -1 && url.pathname === '/') {
            return {
                valid: false,
                message: t('Please add the full address of your traffic source'),
            };
        }

        return {
            valid: true,
        };
    } catch (error) {
        console.error(error);

        return {
            valid: false,
            message: t('Wrong url address'),
        };
    }
};
